/**
 * Repository overlay (`/repository`) — one global surface that merges search
 * with browse across the former Documents page (tier-scoped, Azure-backed
 * library/restricted/workshops documents) and the per-workshop Repository (the
 * decisions/actions/assumptions/transcripts/captures/uploads a workshop
 * accumulates).
 *
 * It is a URL-driven modal: the `/repository` route mounts it open, so
 * deep-links (`?ws=WS4`), refresh, and browser-back all keep working, while
 * ESC / click-out / CLOSE dismiss it back to wherever you came from
 * (`location.state.from`, falling back to Mission Control) — no separate
 * "back" hunt. Reached from the single HudHeader "Repository" entry (and
 * Cmd/Ctrl+K); on a workshop page that entry deep-links here pre-filtered.
 *
 * Search lives *inside* the surface: the box at the top filters everything.
 * Empty query → browse (documents + this workshop's records). A query or
 * active tag facet → unified results (`RepositorySearchResults`); clicking a
 * hit reveals its home here rather than navigating away. The workshop is a
 * *filter*: pick one to scope the record sections + the local search. The
 * decision-log + captures editors are mounted locally so the read-through
 * shortcuts work off-dashboard.
 *
 * Replaces the standalone Documents page and the separate SearchModal.
 */

import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CloudUpload, Library, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { isWorkshopBffEnabled } from "@/env";
import { migrateLocalWorkshopDataToBff } from "@/lib/migrate-workshop-data";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { Button } from "@/components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { TagFilter } from "@/components/tags/TagFilter";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { DocumentsPanel } from "@/features/documents/DocumentsPanel";
import { DocumentPreviewDialog, UploadPreviewDialog } from "@/features/documents/DocumentPreviewDialog";
import { DecisionLogModal } from "@/features/workshop/modals/DecisionLogModal";
import { CapturesModal } from "@/features/ws4-captures/CapturesModal";
import { RepositorySearchResults } from "@/features/search/RepositorySearchResults";
import { useJumpTarget, logTabForCategory } from "@/features/search/useJumpTarget";
import type { SearchHit } from "@/lib/search";
import type { SearchHit as DocsSearchHit } from "@/lib/docs-api";
import { collectTags } from "@/lib/tags";
import { stripNs, TAG_NS_OPPORTUNITY } from "@/lib/taxonomy";
import { useCaptures } from "@/stores/useCaptures";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useTranscripts } from "@/stores/useTranscripts";
import { useAssetLocker, type Upload } from "@/stores/useAssetLocker";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import {
  WorkshopRecordsView,
  useWorkshopRecordCount,
} from "@/features/repository/record-sections";

const DEFAULT_WS: Workshop["id"] = "WS1";

function isWorkshopId(value: string | null): value is Workshop["id"] {
  return !!value && WORKSHOPS.some((w) => w.id === value);
}

/** Overlay default when no `?ws` deep-link is present: the last workshop
 *  dashboard the user visited (written by `WorkshopDashboard`), so records
 *  added from Mission Control / Cmd+K land in the workshop they were just
 *  working in — not silently in WS1's bucket. */
function defaultWorkshopId(): Workshop["id"] {
  const last = sessionStorage.getItem("xaes:last-workshop");
  return isWorkshopId(last) ? last : DEFAULT_WS;
}

export function RepositoryOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  // Where the user opened the overlay from — so closing returns them there
  // rather than a hardcoded route. Survives in history state; absent on a
  // cold deep-link / refresh, where Mission Control is the sane fallback.
  const from = (location.state as { from?: string } | null)?.from;
  const [searchParams, setSearchParams] = useSearchParams();
  const wsParam = searchParams.get("ws");
  const wsFilter: Workshop["id"] = isWorkshopId(wsParam)
    ? wsParam
    : defaultWorkshopId();

  const recordCount = useWorkshopRecordCount(wsFilter);
  const [logOpen, setLogOpen] = useState(false);
  const [capturesOpen, setCapturesOpen] = useState(false);
  const [docPreview, setDocPreview] = useState<DocsSearchHit | null>(null);
  const [uploadPreview, setUploadPreview] = useState<Upload | null>(null);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const setJumpTarget = useJumpTarget((s) => s.set);
  const bffMode = isWorkshopBffEnabled();
  const hasQuery = query.trim().length > 0;
  const isSearching = hasQuery || activeTags.length > 0;

  // Tag chips for the selected workshop (same sources as browse TagFilter).
  const captures = useCaptures((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const decisions = useDecisions((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const actions = useActions((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const assumptions = useAssumptions((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const transcripts = useTranscripts((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const uploads = useAssetLocker((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const findings = useFindings((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const opportunities = useOpportunities((s) => s.entriesByWorkshop[wsFilter] ?? []);
  const recommendations = useRecommendations(
    (s) => s.entriesByWorkshop[wsFilter] ?? [],
  );
  const notes = useFacilitationNotes((s) => s.entriesByWorkshop[wsFilter] ?? []);

  const allTags = useMemo(
    () =>
      collectTags([
        ...uploads,
        ...captures,
        ...decisions,
        ...actions,
        ...assumptions,
        ...transcripts,
        ...findings,
        ...opportunities,
        ...recommendations,
        ...notes,
      ]),
    [
      uploads,
      captures,
      decisions,
      actions,
      assumptions,
      transcripts,
      findings,
      opportunities,
      recommendations,
      notes,
    ],
  );

  const close = () => navigate(from ?? "/");

  const setWorkshop = (id: Workshop["id"]) => {
    const next = new URLSearchParams(searchParams);
    next.set("ws", id);
    // Preserve the `from` state so closing after re-filtering still returns
    // to the opener.
    setSearchParams(next, { replace: true, state: location.state });
  };

  const clearSearch = () => {
    setQuery("");
    setActiveTags([]);
  };

  const onUploadLocal = async () => {
    setUploading(true);
    try {
      const records = await migrateLocalWorkshopDataToBff();
      toast.success(
        records > 0
          ? `Uploaded ${records} record${records === 1 ? "" : "s"} and synced your files`
          : "Synced your files — no local records to upload",
      );
    } catch {
      toast.error("Upload failed — some items may not have synced");
    } finally {
      setUploading(false);
    }
  };

  // A clicked result reveals its home in this surface (we're already in the
  // Repository — no navigation). Records edit/preview in their local editors;
  // uploads open an in-place preview (Asset Locker isn't mounted here).
  // The rAF defer stops the triggering click reaching the newly-opened nested
  // dialog's dismissable layer as an "outside press" (which would instantly
  // re-close it).
  const handleRecordHit = (hit: SearchHit) => {
    if (hit.category === "upload") {
      const upload = uploads.find((u) => u.id === hit.id) ?? null;
      clearSearch();
      if (upload) {
        requestAnimationFrame(() => setUploadPreview(upload));
      }
      return;
    }
    clearSearch();
    setJumpTarget({ workshopId: wsFilter, category: hit.category, id: hit.id });
    if (hit.category === "capture") {
      requestAnimationFrame(() => setCapturesOpen(true));
    } else if (logTabForCategory(hit.category)) {
      requestAnimationFrame(() => setLogOpen(true));
    }
    // transcript: clearing the query reveals it in the records column.
  };
  const handleDocHit = (hit: DocsSearchHit) => {
    clearSearch();
    requestAnimationFrame(() => setDocPreview(hit));
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <PanelDialogContent
        className="w-[94vw] max-w-6xl border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogDescription className="sr-only">
          Search and browse mission documents and everything your workshops
          capture. Pick a workshop to filter its records.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{
            width: "100%",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER — title + close */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Library
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
              />
              <DialogTitle className="modal-title" style={{ margin: 0 }}>
                Repository
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={close}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          {/* CONTENT — fixed toolbar + scrollable body */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              padding: "16px 18px",
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Toolbar: search (filters everything) + workshop filter + upload */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div style={{ position: "relative", flex: "1 1 280px", minWidth: 0 }}>
                <Search
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 16,
                    height: 16,
                    color: "var(--text-muted)",
                    pointerEvents: "none",
                  }}
                />
                <input
                  autoFocus
                  type="search"
                  aria-label={
                    bffMode
                      ? "Search documents and workshop records"
                      : "Search this workshop"
                  }
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    bffMode
                      ? "Search by filename or content across documents and workshop records…"
                      : "Search captures, decisions, actions, assumptions, transcripts, uploads…"
                  }
                  className={FIELD_CLASS}
                  style={{ paddingLeft: 34 }}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Workshop</span>
                <select
                  value={wsFilter}
                  onChange={(e) => setWorkshop(e.target.value as Workshop["id"])}
                  aria-label="Filter records by workshop"
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                >
                  {WORKSHOPS.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.longTitle}
                    </option>
                  ))}
                </select>
              </label>
              {bffMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void onUploadLocal()}
                  disabled={uploading}
                  title="Push records & files captured offline (in this browser) up to the server"
                >
                  {uploading ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <CloudUpload className="size-4" />
                  )}
                  Upload my local data
                </Button>
              )}
            </div>

            {allTags.length > 0 && (
              <TagFilter
                allTags={allTags}
                selected={activeTags}
                onChange={setActiveTags}
                workshopId={wsFilter}
                formatLabel={(t) => stripNs(TAG_NS_OPPORTUNITY, t)}
              />
            )}

            {/* Body — search results when querying; otherwise the browse grid.
                The browse stays mounted (just hidden) so its document tier
                selection + the hydrated record stores survive a search. */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {isSearching && (
                <RepositorySearchResults
                  query={query}
                  activeTags={activeTags}
                  workshopId={wsFilter}
                  onOpenRecordHit={handleRecordHit}
                  onOpenDocHit={handleDocHit}
                />
              )}
              <div
                className="grid gap-6 lg:grid-cols-2"
                style={{ display: isSearching ? "none" : undefined }}
              >
                {/* Documents — global, tier-scoped (library / restricted / workshops). */}
                <section className="rounded-lg border border-border bg-card/40 backdrop-blur-sm">
                  <h2 className="border-b border-border px-4 py-3 text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                    Documents
                  </h2>
                  <DocumentsPanel />
                </section>

                {/* Workshop content — scoped to the selected workshop. */}
                <section className="rounded-lg border border-border bg-card/40 p-4 backdrop-blur-sm">
                  <h2 className="mb-3 flex items-center justify-between text-sm font-semibold tracking-wide uppercase text-muted-foreground">
                    <span>
                      {WORKSHOPS.find((w) => w.id === wsFilter)?.longTitle ?? wsFilter}
                    </span>
                    <span className="text-xs font-normal normal-case">
                      {recordCount} item{recordCount === 1 ? "" : "s"}
                    </span>
                  </h2>
                  <WorkshopRecordsView
                    workshopId={wsFilter}
                    onEditLog={() => setLogOpen(true)}
                    onOpenCaptures={() => setCapturesOpen(true)}
                  />
                </section>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Repository</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws" style={{ color: "var(--accent-sky)" }}>
              {wsFilter}
            </span>
          </div>
        </div>
      </PanelDialogContent>

      {/* Editors mounted locally, scoped to the selected workshop, so the
          read-through shortcuts work without the workshop dashboard. */}
      <DecisionLogModal open={logOpen} onOpenChange={setLogOpen} workshopId={wsFilter} />
      <CapturesModal open={capturesOpen} onOpenChange={setCapturesOpen} workshopId={wsFilter} />
      {docPreview && (
        <DocumentPreviewDialog hit={docPreview} onClose={() => setDocPreview(null)} />
      )}
      {uploadPreview && (
        <UploadPreviewDialog
          upload={uploadPreview}
          onClose={() => setUploadPreview(null)}
        />
      )}
    </Dialog>
  );
}
